import type { LastAction } from '../schemas/release-component';
import type { ReleaseOperation } from '../schemas/release-operation';

/**
 * The page's operation status as plain data, for the reason `component-card.ts`
 * gives: vitest runs on `node`, so a label built inside a component is a label no
 * test can see.
 *
 * The label always renders from the stored `lastAction`, never from current flag
 * values and never from the operation rows, which expire (design N1).
 */

type State = LastAction['state'];

/** Design B3: 3 s while running, 30 s while stalled so a DLQ redrive is still seen. */
export const POLL_RUNNING_MS = 3000;
export const POLL_STALLED_MS = 30_000;

export function isTerminal(state: State): boolean {
    return state !== 'running';
}

/**
 * `refetchInterval` for the operation query. `missing` is a 404 (the rows are
 * gone) and ends polling; any other failure keeps polling at the slow cadence so
 * a blip recovers without a reload.
 */
export function pollInterval(
    operation: Pick<ReleaseOperation, 'state' | 'stalled'> | undefined,
    failure: 'none' | 'missing' | 'error' = 'none',
): number | false {
    if (failure === 'missing') return false;
    if (operation === undefined) return failure === 'error' ? POLL_STALLED_MS : POLL_RUNNING_MS;
    if (isTerminal(operation.state)) return false;
    return operation.stalled ? POLL_STALLED_MS : POLL_RUNNING_MS;
}

/** The server's verdict; the web does not reimplement the retry window. */
export function canRetry(operation: Pick<ReleaseOperation, 'retryable'> | undefined): boolean {
    return operation?.retryable === true;
}

/**
 * "Active for all clients", "Active for 3 clients", "Off for 2 clients", "In progress", "Stalled", with
 * " (partial)" appended to a partial result. Null when nothing was ever run.
 */
export function lastActionLabel(
    action: LastAction | undefined,
    options: { readonly stalled?: boolean } = {},
): string | null {
    if (action === undefined) return null;
    if (action.state === 'running') return options.stalled === true ? 'Stalled' : 'In progress';

    const base =
        action.scope === 'all'
            ? action.enabled
                ? 'Active for all clients'
                : 'Off for all clients'
            : `${action.enabled ? 'Active' : 'Off'} for ${String(action.count)} ${
                  action.count === 1 ? 'client' : 'clients'
              }`;
    return action.state === 'partial' ? `${base} (partial)` : base;
}

export interface OperationStatusView {
    readonly label: string | null;
    readonly polling: number | false;
    /** Counters are available (the operation rows still exist). */
    readonly showResults: boolean;
    readonly showRetry: boolean;
    readonly note?: string;
}

const START_NEW = 'Start a new operation.';

/**
 * What the status area shows for a last action and its polled operation.
 * `operationMissing` is the 404 case: the label stays, results and retry go, and
 * polling stops. A running last action whose operation is gone is an abandoned
 * lock, shown as "Stalled" without retry (the next start closes it).
 */
export function operationStatusView(input: {
    readonly lastAction: LastAction | undefined;
    readonly operation?: ReleaseOperation | undefined;
    readonly operationMissing?: boolean;
}): OperationStatusView {
    const { lastAction, operation, operationMissing = false } = input;
    if (lastAction === undefined) {
        return { label: null, polling: false, showResults: false, showRetry: false };
    }

    if (operationMissing) {
        const running = lastAction.state === 'running';
        return {
            label: running ? 'Stalled' : lastActionLabel(lastAction),
            polling: false,
            showResults: false,
            showRetry: false,
            note: running
                ? `This operation can no longer be found. ${START_NEW}`
                : 'Details are no longer available.',
        };
    }

    // The polled state is fresher than the detail until it refetches; only trust it for the same action.
    if (operation === undefined || operation.operationId !== lastAction.operationId) {
        return {
            label: lastActionLabel(lastAction),
            polling: false,
            showResults: false,
            showRetry: false,
        };
    }

    const retry = canRetry(operation);
    return {
        label: lastActionLabel(
            { ...lastAction, state: operation.state },
            {
                stalled: operation.stalled,
            },
        ),
        polling: pollInterval(operation),
        showResults: true,
        showRetry: retry,
        ...(operation.stalled && !retry
            ? { note: `It can no longer be retried. ${START_NEW}` }
            : {}),
    };
}
